import { rendererCapacity } from "./renderer-capacity";

type ObjectPacket = {
  nodes: number[];
  connections: number[];
  inputIndices: number[];
  outputIndices: number[];
};

type PackedProbeTiming = {
  candidateCount: number;
  payloadBytes: number;
  jsPreparationMs: number;
  uploadMs: number;
  gpuWaitMs: number;
  totalMs: number;
  payloadRepresentation: string;
  typedArrayViews: boolean;
};

type ProbeResources = {
  gl: WebGL2RenderingContext;
  nodeTexture: WebGLTexture;
  connectionTexture: WebGLTexture;
  nodeData: Float32Array;
  connectionData: Float32Array;
  inputIndices: Int32Array;
  outputIndices: Int32Array;
};

type PackedRecord = {
  nodes: Float32Array;
  connections: Float32Array;
  typedArrayViews: boolean;
};

const { maxNodes, maxConnections } = rendererCapacity;
const probeResources = new WeakMap<HTMLCanvasElement, ProbeResources>();

function createFloatTexture(gl: WebGL2RenderingContext, width: number): WebGLTexture {
  const texture = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, width, 1, 0, gl.RGBA, gl.FLOAT, null);
  return texture;
}

function getProbeResources(canvasId: string): ProbeResources {
  const canvas = document.getElementById(canvasId) as HTMLCanvasElement | null;
  if (!canvas) throw new Error("Packed-path probe canvas is missing.");
  const existing = probeResources.get(canvas);
  if (existing) return existing;
  const gl = canvas.getContext("webgl2", { antialias: false });
  if (!gl) throw new Error("WebGL2 is unavailable for the packed-path probe.");
  const resources: ProbeResources = {
    gl,
    nodeTexture: createFloatTexture(gl, maxNodes),
    connectionTexture: createFloatTexture(gl, maxConnections),
    nodeData: new Float32Array(maxNodes * 4),
    connectionData: new Float32Array(maxConnections * 4),
    inputIndices: new Int32Array(6),
    outputIndices: new Int32Array(3)
  };
  probeResources.set(canvas, resources);
  return resources;
}

function measureObjectBatch(packets: ObjectPacket[], canvasId = "phase15-packed-canvas"): PackedProbeTiming {
  const resources = getProbeResources(canvasId ?? "phase15-packed-canvas");
  const totalStarted = performance.now();
  let jsPreparationMs = 0;
  let uploadMs = 0;
  const gl = resources.gl;
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, resources.nodeTexture);
  gl.activeTexture(gl.TEXTURE1);
  gl.bindTexture(gl.TEXTURE_2D, resources.connectionTexture);

  for (const packet of packets) {
    const preparationStarted = performance.now();
    resources.nodeData.set(packet.nodes);
    resources.connectionData.set(packet.connections);
    resources.inputIndices.set(packet.inputIndices);
    resources.outputIndices.set(packet.outputIndices);
    jsPreparationMs += performance.now() - preparationStarted;

    const uploadStarted = performance.now();
    const nodeCount = packet.nodes.length / 4;
    const connectionCount = packet.connections.length / 4;
    gl.activeTexture(gl.TEXTURE0);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, nodeCount, 1, gl.RGBA, gl.FLOAT, resources.nodeData, 0);
    gl.activeTexture(gl.TEXTURE1);
    if (connectionCount > 0)
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, connectionCount, 1, gl.RGBA, gl.FLOAT, resources.connectionData, 0);
    uploadMs += performance.now() - uploadStarted;
  }

  const waitStarted = performance.now();
  gl.finish();
  const gpuWaitMs = performance.now() - waitStarted;
  return {
    candidateCount: packets.length,
    payloadBytes: packets.reduce((total, packet) => total + packet.nodes.length * 4 + packet.connections.length * 4 + 9 * 4, 0),
    jsPreparationMs,
    uploadMs,
    gpuWaitMs,
    totalMs: performance.now() - totalStarted,
    payloadRepresentation: "object with numeric arrays",
    typedArrayViews: false
  };
}

function toBytes(payload: unknown): { bytes: Uint8Array; representation: string } {
  if (payload instanceof Uint8Array)
    return { bytes: payload, representation: "Uint8Array" };
  if (payload instanceof ArrayBuffer)
    return { bytes: new Uint8Array(payload), representation: "ArrayBuffer" };
  if (typeof payload === "string") {
    const binary = atob(payload);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index);
    return { bytes, representation: "base64 string" };
  }
  if (Array.isArray(payload))
    return { bytes: Uint8Array.from(payload), representation: "number array" };
  throw new Error("Packed CPPN payload was not received as bytes.");
}

function readUint32(view: DataView, offset: number): number {
  return view.getUint32(offset, true);
}

function floatView(bytes: Uint8Array, offset: number, count: number): { values: Float32Array; aliased: boolean } {
  const byteLength = count * 4;
  const absoluteOffset = bytes.byteOffset + offset;
  if (absoluteOffset % 4 === 0)
    return { values: new Float32Array(bytes.buffer, absoluteOffset, count), aliased: true };
  return { values: new Float32Array(bytes.slice(offset, offset + byteLength).buffer), aliased: false };
}

function parsePackedBatch(bytes: Uint8Array): { records: PackedRecord[]; typedArrayViews: boolean } {
  if (bytes.byteLength < 20) throw new Error("Packed CPPN payload is truncated.");
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (readUint32(view, 0) !== 0x31435050 || readUint32(view, 4) !== 1)
    throw new Error("Packed CPPN payload header is not recognized.");
  const candidateCount = readUint32(view, 8);
  const directoryOffset = readUint32(view, 12);
  if (readUint32(view, 16) !== bytes.byteLength || directoryOffset + candidateCount * 24 > bytes.byteLength)
    throw new Error("Packed CPPN payload directory is invalid.");

  const records: PackedRecord[] = [];
  let allAliased = true;
  for (let index = 0; index < candidateCount; index++) {
    const entry = directoryOffset + index * 24;
    const nodeCount = readUint32(view, entry);
    const connectionCount = readUint32(view, entry + 4);
    const nodeOffset = readUint32(view, entry + 8);
    const connectionOffset = readUint32(view, entry + 12);
    const inputOffset = readUint32(view, entry + 16);
    const outputOffset = readUint32(view, entry + 20);
    const nodeEnd = nodeOffset + nodeCount * 16;
    const connectionEnd = connectionOffset + connectionCount * 16;
    if (nodeCount > maxNodes || connectionCount > maxConnections || nodeEnd > bytes.byteLength || connectionEnd > bytes.byteLength || inputOffset + 24 > bytes.byteLength || outputOffset + 12 > bytes.byteLength)
      throw new Error("Packed CPPN payload record exceeds the probe capacity.");
    const nodes = floatView(bytes, nodeOffset, nodeCount * 4);
    const connections = floatView(bytes, connectionOffset, connectionCount * 4);
    allAliased = allAliased && nodes.aliased && connections.aliased;
    records.push({ nodes: nodes.values, connections: connections.values, typedArrayViews: nodes.aliased && connections.aliased });
  }
  return { records, typedArrayViews: allAliased };
}

function measurePackedBatch(payload: unknown, canvasId = "phase15-packed-canvas"): PackedProbeTiming {
  const resources = getProbeResources(canvasId ?? "phase15-packed-canvas");
  const totalStarted = performance.now();
  const preparationStarted = performance.now();
  const received = toBytes(payload);
  const parsed = parsePackedBatch(received.bytes);
  const records = parsed.records;
  const jsPreparationMs = performance.now() - preparationStarted;
  const gl = resources.gl;
  let uploadMs = 0;
  for (const record of records) {
    const uploadStarted = performance.now();
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, resources.nodeTexture);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, record.nodes.length / 4, 1, gl.RGBA, gl.FLOAT, record.nodes, 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, resources.connectionTexture);
    if (record.connections.length > 0)
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, record.connections.length / 4, 1, gl.RGBA, gl.FLOAT, record.connections, 0);
    uploadMs += performance.now() - uploadStarted;
  }
  const waitStarted = performance.now();
  gl.finish();
  const gpuWaitMs = performance.now() - waitStarted;
  return {
    candidateCount: records.length,
    payloadBytes: received.bytes.byteLength,
    jsPreparationMs,
    uploadMs,
    gpuWaitMs,
    totalMs: performance.now() - totalStarted,
    payloadRepresentation: received.representation,
    typedArrayViews: parsed.typedArrayViews
  };
}

function compareGalleryParity(objectPackets: ObjectPacket[], packedPayload: unknown): { match: boolean; pixelCount: number; mismatchedPixels: number; maxChannelDelta: number } {
  window.patternEvolverRenderer.renderGallery("phase15-object-canvas", "phase15-object-render-status", objectPackets, 0, 1);
  window.patternEvolverRenderer.renderGalleryPacked("phase15-packed-render-canvas", "phase15-packed-render-status", packedPayload, 0, 1);
  const objectCanvas = document.getElementById("phase15-object-canvas") as HTMLCanvasElement | null;
  const packedCanvas = document.getElementById("phase15-packed-render-canvas") as HTMLCanvasElement | null;
  const objectGl = objectCanvas?.getContext("webgl2", { antialias: false });
  const packedGl = packedCanvas?.getContext("webgl2", { antialias: false });
  if (!objectCanvas || !packedCanvas || !objectGl || !packedGl) throw new Error("Parity canvases could not be initialized.");
  const objectPixels = new Uint8Array(objectCanvas.width * objectCanvas.height * 4);
  const packedPixels = new Uint8Array(packedCanvas.width * packedCanvas.height * 4);
  objectGl.readPixels(0, 0, objectCanvas.width, objectCanvas.height, objectGl.RGBA, objectGl.UNSIGNED_BYTE, objectPixels);
  packedGl.readPixels(0, 0, packedCanvas.width, packedCanvas.height, packedGl.RGBA, packedGl.UNSIGNED_BYTE, packedPixels);
  let mismatchedPixels = 0;
  let maxChannelDelta = 0;
  for (let offset = 0; offset < objectPixels.length; offset += 4) {
    let pixelMismatch = false;
    for (let channel = 0; channel < 4; channel++) {
      const delta = Math.abs(objectPixels[offset + channel] - packedPixels[offset + channel]);
      maxChannelDelta = Math.max(maxChannelDelta, delta);
      pixelMismatch = pixelMismatch || delta !== 0;
    }
    if (pixelMismatch) mismatchedPixels++;
  }
  return { match: mismatchedPixels === 0, pixelCount: objectCanvas.width * objectCanvas.height, mismatchedPixels, maxChannelDelta };
}

window.phase15PackedPath = { measureObjectBatch, measurePackedBatch, compareGalleryParity };

declare global {
  interface Window {
    phase15PackedPath: {
      measureObjectBatch(packets: ObjectPacket[], canvasId?: string): PackedProbeTiming;
      measurePackedBatch(payload: unknown, canvasId?: string): PackedProbeTiming;
      compareGalleryParity(objectPackets: ObjectPacket[], packedPayload: unknown): { match: boolean; pixelCount: number; mismatchedPixels: number; maxChannelDelta: number };
    };
    patternEvolverRenderer: {
      renderGallery(canvasId: string, statusId: string, packets: ObjectPacket[], compileMs?: number, qualityScale?: number): unknown;
      renderGalleryPacked(canvasId: string, statusId: string, payload: unknown, compileMs?: number, qualityScale?: number): unknown;
    };
  }
}
